import React, { useEffect, useRef } from 'react'
import { Button } from 'antd'
import styled from 'styled-components/macro'
import {
  trackSupplierInviteFunnel,
  SUPPLIER_INVITE_FUNNEL,
} from './supplierInviteAnalytics'

/**
 * Compact, benefit-led invite entry point for supplier Orders (and similar).
 * Keeps order management unobstructed — no auto-popup.
 */
const InviteCustomerPrompt = ({ onInvite, onViewProgress, supplierId }) => {
  const viewedRef = useRef(false)

  useEffect(() => {
    if (viewedRef.current) return
    viewedRef.current = true
    trackSupplierInviteFunnel({
      eventName: SUPPLIER_INVITE_FUNNEL.PANEL_VIEWED,
      supplierId,
      metadata: { surface: 'orders' },
    })
  }, [supplierId])

  return (
    <Panel>
      <Copy>
        <Title>Help another customer order from you on OMS</Title>
        <Body>
          Give your customers a shared ordering list with your products and
          prices. Ordering is free for them.
        </Body>
      </Copy>
      <Actions>
        <PrimaryButton
          id='btn-orders-invite-customer'
          type='primary'
          onClick={onInvite}
        >
          Invite a customer
        </PrimaryButton>
        <SecondaryButton
          id='btn-orders-view-customer-progress'
          onClick={onViewProgress}
        >
          View customer progress
        </SecondaryButton>
      </Actions>
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border-radius: 10px;
  background: rgba(165, 214, 63, 0.12);
  border: 1px solid rgba(165, 214, 63, 0.35);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (min-width: 890px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  @media (max-width: 700px) {
    padding: 12px;
  }
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.35;
`

const Body = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #4b5563;
  max-width: 520px;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 700px) {
    width: 100%;

    button {
      flex: 1 1 auto;
      min-width: 140px;
    }
  }
`

const PrimaryButton = styled(Button)`
  && {
    background: ${({ theme }) => theme.colours.lettuceGreen};
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    color: #fff;
    font-weight: 600;
    height: 36px;
    padding: 0 14px;

    &:hover,
    &:focus {
      background: ${({ theme }) => theme.colours.lettuceGreen};
      border-color: ${({ theme }) => theme.colours.lettuceGreen};
      color: #fff;
      opacity: 0.9;
    }
  }
`

const SecondaryButton = styled(Button)`
  && {
    height: 36px;
    padding: 0 14px;
    font-weight: 500;
  }
`

export default InviteCustomerPrompt
